import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, acceptInvite, logout } = useAuth();

  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState("");
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setVerifyError("No invite token provided");
      setVerifying(false);
      return;
    }
    api
      .get(`/invites/verify/${token}`)
      .then(({ data }) => {
        if (!active) return;
        setInvite(data);
        setName(data.name || "");
      })
      .catch((e) => {
        if (!active) return;
        setVerifyError(formatApiError(e.response?.data?.detail) || "Invalid invite");
      })
      .finally(() => active && setVerifying(false));
    return () => {
      active = false;
    };
  }, [token]);

  // A user can land here while already signed in — typically because auto-login
  // is on for the seed admin, or they were already a registered client. The
  // invite is for a NEW account though, so we mustn't silently send them home;
  // they need to either sign out first or recognise it's a self-invite.
  const signOutAndStay = async () => {
    setSigningOut(true);
    await logout();
    setSigningOut(false);
  };

  // If the invite is meant for the SAME user who's currently signed in,
  // the right move is to send them home (this happens when an admin
  // re-clicks their own invite link out of curiosity).
  if (user && invite && user.email && invite.email && user.email.toLowerCase() === invite.email.toLowerCase()) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    const res = await acceptInvite(token, name, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome aboard, " + (name || invite?.email));
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F7F8] p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo className="h-12 w-auto" />
        </div>
        <div className="sos-surface rounded-lg p-6">
          <h1 className="font-display text-2xl font-semibold text-[#011836] mb-1">Accept Invitation</h1>
          <p className="text-sm text-[#5B6B7C] mb-6">
            Set your password to join the Southern Ocean Solutions workspace.
          </p>

          {verifying && (
            <div className="text-[#5B6B7C] text-sm" data-testid="invite-verifying">
              Verifying invite…
            </div>
          )}

          {!verifying && verifyError && (
            <div
              className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-3 rounded"
              data-testid="invite-error"
            >
              {verifyError}
            </div>
          )}

          {!verifying && !verifyError && invite && (
            <>
              {user && (
                <div
                  className="mb-4 rounded-lg border border-amber-400/30 bg-amber-500/[0.06] px-3 py-3 text-sm"
                  data-testid="invite-session-warning"
                >
                  <div className="text-amber-200 font-display font-semibold mb-1">
                    You&rsquo;re currently signed in as {user.email}
                  </div>
                  <div className="text-[#5B6B7C] text-xs mb-3">
                    This invite is for <span className="text-[#011836]">{invite.email}</span>. Sign out first to accept it as the invited user.
                  </div>
                  <Button
                    type="button"
                    onClick={signOutAndStay}
                    disabled={signingOut}
                    size="sm"
                    className="bg-amber-500/90 hover:bg-amber-500 text-[#0b0e12] font-display font-semibold"
                    data-testid="invite-signout-btn"
                  >
                    {signingOut ? "Signing out…" : `Sign out and continue as ${invite.email}`}
                  </Button>
                </div>
              )}
              <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Email</Label>
                <div className="mt-1 text-[#011836] font-display" data-testid="invite-email">
                  {invite.email}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Your name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  New password
                </Label>
                <Input
                  id="pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="invite-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cpw" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Confirm password
                </Label>
                <Input
                  id="cpw"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="invite-confirm-input"
                />
              </div>
              {error && (
                <div
                  className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-2 rounded"
                  data-testid="invite-submit-error"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting || !!user}
                className="w-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide disabled:opacity-40"
                data-testid="invite-submit-btn"
                title={user ? "Sign out first to accept this invite" : ""}
              >
                {submitting ? "Joining…" : "Accept & sign in"}
              </Button>
            </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
